import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import { setMarket } from "../../../redux/slices/uiSlice";
import {
  completePreparationRequest,
  createPreparationRequest,
  fetchPreparationRequests,
} from "../../../api/preparationRequests";

const TYPE_LABELS = { labels: "Печать этикеток", kiz: "Честный знак" };
const TYPE_COLORS = {
  labels: { bgcolor: "#D8F0FF", color: "#075985" },
  kiz: { bgcolor: "#FFE15A", color: "#473600" },
};

function formatDate(value) {
  return value ? new Date(value).toLocaleString("ru-RU") : "—";
}

export default function PreparationRequestsPage() {
  const market = useSelector((state) => state.ui.market);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState("pending");
  const [taskType, setTaskType] = useState("labels");
  const [title, setTitle] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [completingId, setCompletingId] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    async function loadRows() {
      try {
        const result = await fetchPreparationRequests(tab, page, pageSize);
        if (active) {
          if (page > 0 && page * pageSize >= result.total) {
            setPage(Math.max(0, Math.ceil(result.total / pageSize) - 1));
            return;
          }
          setRows(result.rows);
          setTotal(result.total);
        }
      } catch (error) {
        if (active) {
          console.error("Не удалось загрузить заявки", error);
          enqueueSnackbar("Не удалось загрузить заявки", { variant: "error" });
        }
      } finally {
        if (active) setLoading(false);
      }
    }
    loadRows();
    return () => { active = false; };
  }, [enqueueSnackbar, page, pageSize, reloadToken, tab]);

  async function handleCreate(event) {
    event.preventDefault();
    const count = Number(quantity);
    if (!title.trim() || !Number.isSafeInteger(count) || count < 1) return;
    setSaving(true);
    try {
      await createPreparationRequest({ taskType, title, quantity: count });
      setTitle("");
      setQuantity("1");
      setTab("pending");
      setPage(0);
      setReloadToken((current) => current + 1);
      enqueueSnackbar("Заявка добавлена", { variant: "success" });
    } catch (error) {
      console.error("Не удалось создать заявку", error);
      enqueueSnackbar("Не удалось создать заявку", { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  async function handleComplete(row) {
    setCompletingId(row.id);
    try {
      await completePreparationRequest(row.id);
      setRows((current) => current.filter((item) => item.id !== row.id));
      setTotal((current) => Math.max(0, current - 1));
      if (rows.length === 1 && page > 0) {
        setPage((current) => current - 1);
      } else {
        setReloadToken((current) => current + 1);
      }
      enqueueSnackbar("Заявка выполнена", { variant: "success" });
    } catch (error) {
      console.error("Не удалось завершить заявку", error);
      enqueueSnackbar("Не удалось завершить заявку", { variant: "error" });
    } finally {
      setCompletingId(null);
    }
  }

  return (
    <DashboardLayout market={market} onSelectMarket={(value) => dispatch(setMarket(value))}>
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" spacing={1}>
            <Box>
              <Typography variant="h6">Заявки на подготовку</Typography>
              <Typography variant="body2" color="text.secondary">
                Оставьте заявку на печать этикеток или работу с «Честным знаком».
              </Typography>
            </Box>
            <Button variant="outlined" onClick={() => setReloadToken((current) => current + 1)} disabled={loading}>
              Обновить
            </Button>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>Новая заявка</Typography>
          <Box component="form" onSubmit={handleCreate}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-start" }}>
              <TextField select label="Тип работы" value={taskType} onChange={(event) => setTaskType(event.target.value)} sx={{ minWidth: 190 }}>
                <MenuItem value="labels">Печать этикеток</MenuItem>
                <MenuItem value="kiz">Честный знак</MenuItem>
              </TextField>
              <TextField label="Название" placeholder="Например, ФайтКлаб 1 штука" value={title} onChange={(event) => setTitle(event.target.value)} required fullWidth inputProps={{ maxLength: 255 }} />
              <TextField label="Количество" type="number" value={quantity} onChange={(event) => setQuantity(event.target.value)} required sx={{ width: { xs: "100%", md: 150 } }} inputProps={{ min: 1, step: 1 }} />
              <Button type="submit" variant="contained" disabled={saving || !title.trim() || !Number.isSafeInteger(Number(quantity)) || Number(quantity) < 1} sx={{ minHeight: 56, whiteSpace: "nowrap" }}>
                {saving ? "Сохранение…" : "Добавить"}
              </Button>
            </Stack>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Tabs value={tab} onChange={(_, value) => { setTab(value); setPage(0); }} sx={{ mb: 2 }}>
            <Tab value="pending" label="В работе" />
            <Tab value="completed" label="Выполненные" />
          </Tabs>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Тип работы</TableCell>
                  <TableCell>Название</TableCell>
                  <TableCell align="right">Количество</TableCell>
                  <TableCell>Создана</TableCell>
                  {tab === "completed" && <TableCell>Выполнена</TableCell>}
                  {tab === "pending" && <TableCell align="right">Действие</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Chip
                        size="small"
                        label={TYPE_LABELS[row.taskType] || row.taskType}
                        sx={{ fontWeight: 600, ...TYPE_COLORS[row.taskType] }}
                      />
                    </TableCell>
                    <TableCell>{row.title}</TableCell>
                    <TableCell align="right">{row.quantity}</TableCell>
                    <TableCell>{formatDate(row.createdAt)}</TableCell>
                    {tab === "completed" && <TableCell>{formatDate(row.completedAt)}</TableCell>}
                    {tab === "pending" && (
                      <TableCell align="right">
                        <Button size="small" variant="contained" onClick={() => handleComplete(row)} disabled={completingId === row.id}>
                          Выполнено
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {loading && <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={22} /></TableCell></TableRow>}
                {!loading && rows.length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    {tab === "pending" ? "Заявок в работе нет" : "Выполненных заявок нет"}
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => {
              setPageSize(Number(event.target.value));
              setPage(0);
            }}
            labelRowsPerPage="Заявок на странице:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
          />
        </Paper>
      </Stack>
    </DashboardLayout>
  );
}
